/**
 * Compare an EARLY and FINAL Daily-Double probables snapshot and return a
 * ranked list of combos sorted by late-money strength.
 *
 * Each snapshot shape:
 *   { pool: number|null, matrix: { "<leg1>": { "<leg2>": number|null, ... } } }
 *
 * The DD takeout rate varies by jurisdiction. 20% is the de-facto NYRA rate
 * and a decent default for other majors. The caller can override it, but
 * either way the willpay-drop metric is takeout-free.
 *
 * Returned combos:
 *   {
 *     leg1, leg2,
 *     earlyWillpay, finalWillpay,
 *     dropPct,          // (early - final) / early  (positive = late money IN)
 *     earlyDollars,     // estimated $ on this combo early
 *     finalDollars,     // estimated $ on this combo at close
 *     dollarDelta,      // finalDollars - earlyDollars
 *     deltaVsProportional, // dollarDelta - earlyDollars * poolGrowth-1
 *   }
 * sorted by dropPct desc.
 */

export const DEFAULT_DD_TAKEOUT = 0.20;

export const compareDdSnapshots = (early, final, takeout = DEFAULT_DD_TAKEOUT) => {
  if (!early?.matrix || !final?.matrix) return [];
  const takeoutMul = 1 - Number(takeout || 0);
  const earlyPool = Number(early.pool) || null;
  const finalPool = Number(final.pool) || null;

  const combos = [];
  for (const [k1, row] of Object.entries(final.matrix)) {
    const earlyRow = early.matrix[k1];
    if (!earlyRow) continue;
    for (const [k2, finalWp] of Object.entries(row)) {
      const earlyWp = earlyRow[k2];
      // Both must be positive numbers to be comparable.
      if (
        earlyWp == null ||
        finalWp == null ||
        !Number.isFinite(Number(earlyWp)) ||
        !Number.isFinite(Number(finalWp)) ||
        Number(earlyWp) <= 0 ||
        Number(finalWp) <= 0
      ) {
        continue;
      }
      const e = Number(earlyWp);
      const f = Number(finalWp);
      const dropPct = (e - f) / e;
      const earlyDollars = earlyPool ? (earlyPool * takeoutMul) / e : null;
      const finalDollars = finalPool ? (finalPool * takeoutMul) / f : null;
      const dollarDelta =
        earlyDollars != null && finalDollars != null
          ? finalDollars - earlyDollars
          : null;
      const poolGrowth =
        earlyPool && finalPool ? finalPool / earlyPool : null;
      const expectedFinal =
        earlyDollars != null && poolGrowth != null
          ? earlyDollars * poolGrowth
          : null;
      const deltaVsProportional =
        finalDollars != null && expectedFinal != null
          ? finalDollars - expectedFinal
          : null;
      combos.push({
        leg1: Number(k1),
        leg2: Number(k2),
        earlyWillpay: e,
        finalWillpay: f,
        dropPct,
        earlyDollars,
        finalDollars,
        dollarDelta,
        deltaVsProportional,
      });
    }
  }
  combos.sort((a, b) => b.dropPct - a.dropPct);
  return combos;
};

export const fmtPct = (n) => {
  if (n == null || !Number.isFinite(n)) return "—";
  const pct = n * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
};

export const fmtDollars = (n) => {
  if (n == null || !Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+$" : n < 0 ? "-$" : "$";
  return `${sign}${Math.abs(n).toFixed(0)}`;
};
